import React, { useRef, useState } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../../util/reactIntl';

import { Form, LocationAutocompleteInput } from '../../../../components';

import IconSearchDesktop from '../TopbarSearchForm/IconSearchDesktop';
import SearchIcon from '../SearchIcon';
import LocationIcon from '../LocationIcon';
import css from './CombinedSearchForm.module.css';

const identity = v => v;

const SEARCH_TYPE_KEYWORD = 'keyword';
const SEARCH_TYPE_LOCATION = 'location';

const KeywordSearchField = props => {
  const { keywordSearchWrapperClasses, iconClass, intl, isMobile = false, inputRef } = props;
  return (
    <div className={keywordSearchWrapperClasses}>
      <button className={css.searchSubmit}>
        <div className={iconClass}>
          <IconSearchDesktop />
        </div>
      </button>
      <Field
        name="keywords"
        render={({ input, meta }) => {
          return (
            <input
              className={isMobile ? css.mobileInput : css.desktopInput}
              {...input}
              id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              ref={inputRef}
              type="text"
              placeholder={intl.formatMessage({
                id: 'CombinedSearchForm.keywordPlaceholder',
              })}
              autoComplete="off"
            />
          );
        }}
      />
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'CombinedSearchForm.locationPlaceholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
          />
        );
      }}
    />
  );
};

/**
 * Combined search form that supports both keyword and location search
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.desktopInputRoot root class for desktop form input
 * @param {Function} props.onSubmit
 * @param {boolean} props.isMobile
 * @param {string} props.searchType - 'keyword' or 'location'
 * @param {Function} props.onSearchTypeChange - callback when search type changes
 * @param {boolean} props.showToggle - whether to show desktop toggle
 * @returns {JSX.Element} search form element
 */
const CombinedSearchForm = props => {
  const searchInputRef = useRef(null);
  const intl = useIntl();
  const { onSubmit, searchType, onSearchTypeChange, showToggle = true, ...restOfProps } = props;

  const onChange = location => {
    if (searchType === SEARCH_TYPE_LOCATION && location.selectedPlace) {
      onSubmit({ location });
      searchInputRef?.current?.blur();
    }
  };

  const onKeywordSubmit = values => {
    if (searchType === SEARCH_TYPE_KEYWORD) {
      onSubmit({ keywords: values.keywords });
      searchInputRef?.current?.blur();
    }
  };

  const isKeywordsSearch = searchType === SEARCH_TYPE_KEYWORD;
  const submit = isKeywordsSearch ? onKeywordSubmit : onSubmit;

  return (
    <FinalForm
      {...restOfProps}
      onSubmit={submit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          desktopInputRoot,
          isMobile = false,
          handleSubmit,
        } = formRenderProps;
        
        const classes = classNames(rootClassName || css.root, className);
        const desktopInputRootClass = css.searchForm;

        const preventFormSubmit = e => e.preventDefault();
        const submitFormFn = isKeywordsSearch ? handleSubmit : preventFormSubmit;

        const keywordSearchWrapperClasses = classNames(
          css.keywordSearchWrapper,
          isMobile ? css.mobileInputRoot : desktopInputRootClass
        );

        const toggleClasses = classNames(css.searchToggle, {
          [css.searchToggleMobile]: isMobile,
        });

        const keywordToggleClasses = classNames(css.toggleButton, {
          [css.toggleButtonActive]: isKeywordsSearch,
        });

        const locationToggleClasses = classNames(css.toggleButton, {
          [css.toggleButtonActive]: !isKeywordsSearch,
        });

        return (
          <Form className={classes} onSubmit={submitFormFn} enforcePagePreloadFor="SearchPage">
            {showToggle && !isMobile && (
              <div className={toggleClasses}>
                <button
                  type="button"
                  className={keywordToggleClasses}
                  onClick={() => onSearchTypeChange(SEARCH_TYPE_KEYWORD)}
                  title={intl.formatMessage({ id: 'CombinedSearchForm.keywordSearch' })}
                >
                  <SearchIcon className={css.toggleIcon} />
                </button>
                <button
                  type="button"
                  className={locationToggleClasses}
                  onClick={() => onSearchTypeChange(SEARCH_TYPE_LOCATION)}
                  title={intl.formatMessage({ id: 'CombinedSearchForm.locationSearch' })}
                >
                  <LocationIcon className={css.toggleIcon} />
                </button>
              </div>
            )}
            <div className={css.searchForm}>
              {isKeywordsSearch ? (
                <KeywordSearchField
                  keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                  iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={searchInputRef}
                />
              ) : (
                <LocationSearchField
                  desktopInputRootClass={desktopInputRootClass}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={searchInputRef}
                  onLocationChange={onChange}
                />
              )}
            </div>
          </Form>
        );
      }}
    />
  );
};

CombinedSearchForm.SEARCH_TYPE_KEYWORD = SEARCH_TYPE_KEYWORD;
CombinedSearchForm.SEARCH_TYPE_LOCATION = SEARCH_TYPE_LOCATION;

export default CombinedSearchForm;